import { useAtomValue } from "jotai";
import first from "lodash/first";
import { type FunctionComponent, startTransition, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import Line from "scmp-app/components/article/article-additional-info-box/line.png";
import type { VariantProps } from "scmp-app/components/article/article-speech/article-speech-inline-widget/variants/types";
import { useArticleSpeechContext } from "scmp-app/components/article/article-speech/contexts";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { postiesArticleSpeechInlineWidgetPostiesArticle$key } from "scmp-app/queries/__generated__/postiesArticleSpeechInlineWidgetPostiesArticle.graphql";

import Pippa from "./img-pippa.png";
import {
  Container,
  Description,
  PippaImage,
  StyledButton,
  StyledLine,
  StyledPlayIcon,
  TextContainer,
  Title,
} from "./styles";

type Props = VariantProps & {
  reference: postiesArticleSpeechInlineWidgetPostiesArticle$key;
};

export const ArticleSpeechInlineWidgetPosties: FunctionComponent<Props> = ({
  className,
  reference,
}) => {
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const article = useFragment(
    graphql`
      fragment postiesArticleSpeechInlineWidgetPostiesArticle on Article {
        entityId
        socialHeadline
        headline
        articleSpeeches {
          country
          gender
          url
          voiceName
        }
        images {
          isSlideshow
          url
        }
      }
    `,
    reference,
  );

  const { setTextToSpeechPlayerState, textToSpeechPlayerState } = useArticleSpeechContext();

  const isShowForPostiesArticle = useMemo(() => {
    const shouldShowPaywall =
      asyncRosettaState?.result?.hasPianoExperienceExecuted &&
      !asyncRosettaState.result?.hasPostiesAccessRight;

    return !shouldShowPaywall;
  }, [
    asyncRosettaState?.result?.hasPostiesAccessRight,
    asyncRosettaState?.result?.hasPianoExperienceExecuted,
  ]);

  const isButtonDisable = useMemo(
    () => textToSpeechPlayerState.status === "playing" || !isShowForPostiesArticle,
    [isShowForPostiesArticle, textToSpeechPlayerState.status],
  );

  const handlePlayClick = () => {
    startTransition(() => {
      const mainImage =
        article.images?.find(image => !!image?.isSlideshow) ?? first(article.images);

      setTextToSpeechPlayerState(previousState => ({
        ...previousState,
        entityId: article.entityId,
        hasTriggeredThreeSeconds: false,
        loaded: 0,
        played: 0,
        src: article.articleSpeeches?.[0]?.url ?? "",
        status: "playing",
        thumbnail: mainImage?.url ?? "",
        title: article.socialHeadline ?? article.headline ?? "",
        voiceNameInfoList: (article.articleSpeeches ?? []).map(speech => ({
          country: speech?.country ?? "",
          gender: speech?.gender ?? "",
          url: speech?.url ?? "",
          voiceName: speech?.voiceName ?? "",
        })),
      }));
    });
  };

  const hasSpeech = !!(article.articleSpeeches ?? [])?.[0]?.url;

  if (!hasSpeech) return null;

  return (
    <Container className={className}>
      <PippaImage src={Pippa.src} />
      <TextContainer>
        <Title>Listen Now</Title>
        <StyledLine src={Line.src} />
        <Description>Enjoy the audio version of this article!</Description>
      </TextContainer>
      <StyledButton
        $isDisabled={isButtonDisable}
        backgroundColor={isButtonDisable ? "#cccccc" : "#fecc17"}
        onClick={() => handlePlayClick()}
        variant="button"
      >
        <StyledPlayIcon />
        Play
      </StyledButton>
    </Container>
  );
};

ArticleSpeechInlineWidgetPosties.displayName = "ArticleSpeechInlineWidgetPosties";
